'use client';

import { env } from '@repo/env';
import { css } from '@repo/pandacss/styled-system/css';
import { type IFrameComponent, iframeResizer } from 'iframe-resizer';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  browserSupportsIframeSrcdoc,
  fetchIframeSrcDoc,
  getSrcValue,
  shouldLoadIframeSrcDoc,
  shouldRenderIframe,
} from './utils';

interface IFrameProps {
  scrolling: 'no';
  ref: (ref: IFrameComponent) => void;
  onLoad: () => void;
  src: string;
  srcDoc?: string;
}

type Props = {
  id?: string;
  src?: string;
  title: string;
  onReady?: () => void;
};

const BaseEmbed = ({ id, src, title, onReady }: Props) => {
  const [ready, setReady] = useState(false);
  const [iframeSrcDoc, setIframeSrcDoc] = useState('');
  const [srcDocFetchComplete, setSrcDocFetchComplete] = useState(false);
  const frameRef = useRef<IFrameComponent | null>(null);
  const visibleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasBeenMadeVisible = useRef(false);

  const setIframeVisible = useCallback(() => {
    if (hasBeenMadeVisible.current) return;
    hasBeenMadeVisible.current = true;
    if (visibleTimeoutRef.current) {
      clearTimeout(visibleTimeoutRef.current);
      visibleTimeoutRef.current = null;
    }

    setReady(true);
    onReady?.();
  }, [onReady]);

  useEffect(() => {
    const onInfogramResize = (e: MessageEvent) => {
      if (!frameRef.current) return false;
      if (frameRef.current.contentWindow !== e.source) return false;

      let data;
      try {
        data = JSON.parse(e.data);
      } catch {
        return false;
      }

      if (data.context !== 'iframe.resize' || !data.height) {
        return false;
      }
      frameRef.current.height = data.height;
    };
    visibleTimeoutRef.current = setTimeout(setIframeVisible, 3000);
    window.addEventListener('message', onInfogramResize);

    return () => {
      window.removeEventListener('message', onInfogramResize);
      if (visibleTimeoutRef.current) clearTimeout(visibleTimeoutRef.current);
    };
  }, [setIframeVisible]);

  useEffect(() => {
    const fetchIframe = async () => {
      const compiledSrc = getSrcValue(id, src);
      if (shouldLoadIframeSrcDoc(compiledSrc)) {
        if (browserSupportsIframeSrcdoc) {
          try {
            const srcDoc = await fetchIframeSrcDoc(compiledSrc);
            setIframeSrcDoc(srcDoc);
            setSrcDocFetchComplete(true);
          } catch (err) {
            if (env.NEXT_PUBLIC_DEPLOYABLE_ENV !== 'prod') {
              console.error('An error occurred in Embed: ', err);
            }
            setSrcDocFetchComplete(true);
          }
        } else {
          setSrcDocFetchComplete(true);
        }
      }
    };

    if (id || src) {
      fetchIframe();
    }

    return () => {
      if (!frameRef.current) return;
      frameRef.current.iFrameResizer.close();
    };
  }, [id, src]);

  const onIframeRef = (ref: IFrameComponent) => {
    if (!ref) return;
    frameRef.current = ref;
    const config = {
      checkOrigin: false,
      log: false,
      onResized: () => {
        setIframeVisible();
      },
    };
    iframeResizer(config, ref);
  };

  const compiledSrc = getSrcValue(id, src);
  if (!compiledSrc) return null;

  const iframeProps: IFrameProps = {
    scrolling: 'no',
    ref: onIframeRef,
    onLoad: () => {
      setIframeVisible();
    },
    src: compiledSrc,
  };
  if (iframeSrcDoc) {
    iframeProps.srcDoc = iframeSrcDoc;
  }

  return (
    <div className={css({ overflowX: 'hidden', overflowY: 'hidden' })}>
      {shouldRenderIframe(compiledSrc, srcDocFetchComplete) ? (
        <iframe
          {...iframeProps}
          title={title}
          style={{
            display: ready ? 'block' : 'none',
            border: 'none',
            width: '1px',
            minWidth: '100%',
          }}
        />
      ) : null}
    </div>
  );
};

export default BaseEmbed;
